import clsx from 'clsx';
import moment from 'moment';
import { getLastAsnGeneratedDateForPlantAndLine,getLastAgsnGeneratedDateForPlantAndLine } from '../apis/calls';
import { DATE_FORMAT } from '../constants';
import { getDatesForRange } from './utils';
import config from '../config/config';
import { AGGREGATE_STATUS_FROM_PIPELINE } from '../constants';

export const getCellStyle = (isDisabled, isEdited, isSunday, styles) => {
  let defaultStyle = styles.mTablecell;
  const modifierStyles = [];
  if (isEdited) modifierStyles.push(styles.editedCell);
  if (isDisabled) modifierStyles.push(styles.disabledCell);
  if (isSunday) modifierStyles.push(styles.sundayCell);
  return clsx(defaultStyle, ...modifierStyles);
};

export const getSelectedOptionalColumns = (
  optionalColumns,
  selectedOptionalColumnName,
  optionalColumnsStyles,
) => {
  return optionalColumns
    .filter(column => selectedOptionalColumnName.includes(column.name))
    .map(column => ({ ...column, ...optionalColumnsStyles }))
    .sort(
      (a, b) =>
        selectedOptionalColumnName.indexOf(a.name) -
        selectedOptionalColumnName.indexOf(b.name),
    );
};

export const loadAsnOverviewDates = (
  plant,
  line,
  setDates,
  setActiveDate,
  defaultStartDate,
  latestPlantValueRef,
  latestLineValueRef,
) => {
  return getLastAsnGeneratedDateForPlantAndLine(plant, line)
    .then(lastAsnGeneratedDate => {
      if (
        (latestPlantValueRef && plant !== latestPlantValueRef.current) ||
        (latestLineValueRef && line !== latestLineValueRef.current)
      )
        return Promise.reject('Stale API data');
      return getDatesForRange(moment().subtract(2, 'days'), lastAsnGeneratedDate);
    })
    .then(
      dates => {
        setDates(dates);
        setActiveDate(defaultStartDate || dates[2]);
      },
      message => console.log(message),
    );
};

export const loadAsnDates = (
  plant,
  line,
  setDates,
  setActiveDate,
  defaultStartDate,
  latestPlantValueRef,
  latestLineValueRef,
) => {
  return getLastAsnGeneratedDateForPlantAndLine(plant, line)
    .then(lastAsnGeneratedDate => {
      if (
        (latestPlantValueRef && plant !== latestPlantValueRef.current) ||
        (latestLineValueRef && line !== latestLineValueRef.current)
      )
        return Promise.reject('Stale API data');
      return getDatesForRange(moment(), lastAsnGeneratedDate);
    })
    .then(
      dates => {
        setDates(dates);
        setActiveDate(defaultStartDate || dates[0]);
      },
      message => console.log(message),
    );
};

export const loadAgsnDates = (
  plant,
  line,
  setDates,
  setActiveDate,
  defaultStartDate,
  latestPlantValueRef,
  latestLineValueRef,
) => {
  return getLastAgsnGeneratedDateForPlantAndLine(plant, line)
    .then(lastAsnGeneratedDate => {
      if (
        (latestPlantValueRef && plant !== latestPlantValueRef.current) ||
        (latestLineValueRef && line !== latestLineValueRef.current)
      )
        return Promise.reject('Stale API data');
        
      return getDatesForRange(moment(), lastAsnGeneratedDate);
    })
    .then(
      dates => {
        setDates(dates);
        setActiveDate(defaultStartDate || dates[0]);
      },
      message => console.log(message),
    );
};

export const splitDescription = description => {
  const descriptionWords = description ? description.split(' ') : [];
  return [
    descriptionWords.slice(0, 2).join(' '),
    descriptionWords.slice(2).join(' '),
  ];
};

export const getFormattedAsn = row => {
  if (!row || !row.asn) return '-';
  const { asn } = row;
  const asnArray = asn.split('-');
  if (asnArray.length > 1 && asnArray[asnArray.length - 1].startsWith('T')) {
    const tValue = asnArray[asnArray.length - 1];
    return `${asnArray[asnArray.length - 2]} ${tValue}`;
  }
  if (asnArray[asnArray.length - 1] === 'A')
    return `${asnArray[asnArray.length - 2]} ${asnArray[asnArray.length - 1]}`;
  return asnArray[asnArray.length - 1];
};

export const getSequenceStartDateString = lastAsnGeneratedDate => {
  const today = moment();
  if (
    lastAsnGeneratedDate &&
    today.isSameOrBefore(lastAsnGeneratedDate, 'day')
  ) {
    return moment(lastAsnGeneratedDate).add(1, 'days').format(DATE_FORMAT.ISO);
  }
  return today.format(DATE_FORMAT.ISO);
};

export const compareByDateAndSerialNumber = (
  a,
  b,
  dateField,
  serialNumberField,
) => {
  const dateA = moment(a[dateField], DATE_FORMAT.ISO);
  const dateB = moment(b[dateField], DATE_FORMAT.ISO);
  if (dateA.isBefore(dateB)) return -1;
  if (dateA.isAfter(dateB)) return 1;
  return a[serialNumberField] - b[serialNumberField];
};

export const getRelativeDateString = date => {
  const dateString = date.format(DATE_FORMAT.ISO);
  const today = moment();
  const yesterday = moment().subtract(1, 'days');
  if (date.isSame(today, 'day')) return 'Today';
  if (date.isSame(yesterday, 'day')) return 'Yesterday';
  return `${moment().diff(
    moment(dateString, DATE_FORMAT.ISO),
    'days',
  )} days ago`;
};

export const filterByVcCurry = searchText => data =>
  !searchText || data.vc.toLowerCase().startsWith(searchText.toLowerCase());

export const filterByVcDescriptionCurry = searchText => data =>
  !searchText ||
  data.vcDescription.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;

export const filterByShiftCurry = searchText => data =>
  !searchText ||
  data.shiftName?.toLowerCase().startsWith(searchText.toLowerCase());

export const filterByNestedVcDescriptionCurry = searchText => row =>
  !searchText ||
  row.vcDescription.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;

export const processSequenceData = sequences => {
  const rows = sequences.map(sequence => {
    const { attributes, serialNumber, ...rest } = sequence;
    return { ...rest, ...attributes, id: String(serialNumber), serialNumber };
  });
  rows.sort((a, b) => a.serialNumber - b.serialNumber);
  return rows;
};

export const processAggregateSequenceData = sequences => {
  sequences.sort((a, b) => a.sequence - b.sequence);
  return sequences;
};

export const formatDataToIncludeColour = data => {
  const formattedData = data.map(record => ({
    ...record,
    colour: record.colourDescription
      ? `${record?.colourDescription} (${record?.colourCode})`
      : null,
  }));
  return formattedData;
};

export const getAggregateStatusAndModifiedAtAndCommentForAvailabilityScreen = material => {
  return {
    ...material,
    aggregateStatus: material.status,
    aggregateModifiedAt: material.modifiedAt,
    aggregateComment: material.comment,
  };
};

export const getAggregateStatusAndModifiedAtAndComment = material =>
  config.AGGREGATE_STATUS ===
  AGGREGATE_STATUS_FROM_PIPELINE.AGGREGATE_STATUS_DEV
    ? {
        ...material,
        aggregateStatus: material.aggregatePlanningStatus || material.status,
        aggregateModifiedAt:
          material.aggregatePlanningModifiedAt || material.modifiedAt,
        aggregateComment: material.aggregatePlanningComment || material.comment,
      }
    : {
        ...material,
        aggregateStatus: material.status,
        aggregateModifiedAt: material.modifiedAt,
        aggregateComment: material.comment,
      };

export const sortByName = (a, b) => {
  if (a.name > b.name) return 1;
  if (a.name < b.name) return -1;
  return 0;
};

export const sortByDate = data => {
  data.sort((a, b) => new Date(a.date) - new Date(b.date));
  return data;
};
